import { useState, useEffect } from 'react';
import RichTextEditor from '../../../components/RichTextEditor';
import StructuredFormBuilder from '../StructuredFormBuilder';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const CORE_ASSIGNMENT_SECTION_NAMES = new Set([
  'Attendance',
  'Critical Questions',
  'Philosophical Diary',
  'Self-Evaluation',
  'Self-Evaluation (Mid)',
  'Self-Evaluation (Final)',
  'Question Improvement'
]);

async function fetchCoreAssignmentSections() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_sections?instructor_id=is.null&select=*,syllabus_quiz_questions(*)&order=section_order.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  const rows = await response.json();

  return Array.isArray(rows)
    ? rows.filter(section =>
        CORE_ASSIGNMENT_SECTION_NAMES.has(section.name)
      )
    : [];
}

async function updateCoreAssignmentSection(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_sections?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  return response.json();
}


async function readRpc(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  const result =
    text ? JSON.parse(text) : null;

  if (result?.ok === false) {
    throw new Error(
      result.message ||
      `${label} failed.`
    );
  }

  return result;
}


async function fetchAssignmentTypes() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_types?is_core=eq.false&is_course_copy=eq.false&select=*,assignment_type_quiz_questions(*),instructors(id,name,email)&order=name.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Loading assignment types failed (${response.status}): ${detail}`
    );
  }

  return response.json();
}


async function saveAssignmentType(
  data,
  cmsToken
) {
  const rpcName =
    data.assignment_kind === 'structured_form'
      ? 'assignment_library_create_structured_type'
      : 'assignment_library_create_type';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${rpcName}`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_name:
          data.name,
        p_instructions:
          data.instructions || '',
        p_assignment_kind:
          data.assignment_kind ||
          'standard',
        p_requires_submission:
          data.requires_submission !==
          false,
        p_allow_groups:
          !!data.allow_groups,
        p_group_submission:
          !!data.group_submission,
        p_syllabus_cooldown_minutes:
          Number(
            data.syllabus_cooldown_minutes
          )
      })
    }
  );

  const result =
    await readRpc(
      response,
      'Creating assignment type'
    );

  return result?.assignment_type;
}


async function updateAssignmentType(
  id,
  data,
  cmsToken
) {
  const rpcName =
    data.assignment_kind === 'structured_form'
      ? 'assignment_library_update_structured_type'
      : 'assignment_library_update_type';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${rpcName}`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_type_id:
          id,
        p_name:
          data.name,
        p_instructions:
          data.instructions || '',
        p_assignment_kind:
          data.assignment_kind ||
          'standard',
        p_requires_submission:
          data.requires_submission !==
          false,
        p_allow_groups:
          !!data.allow_groups,
        p_group_submission:
          !!data.group_submission,
        p_syllabus_cooldown_minutes:
          Number(
            data.syllabus_cooldown_minutes
          )
      })
    }
  );

  const result =
    await readRpc(
      response,
      'Updating assignment type'
    );

  return result?.assignment_type;
}


async function deleteAssignmentType(
  id,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/assignment_library_delete_type`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_type_id:
          id
      })
    }
  );

  await readRpc(
    response,
    'Deleting assignment type'
  );
}

async function saveQuizQuestion(
  data,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/assignment_library_add_quiz_question`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_type_id:
          data.assignment_type_id,
        p_question:
          data.question,
        p_choices:
          data.choices,
        p_correct_answer_index:
          data.correct_answer_index,
        p_correct_answer:
          data.correct_answer,
        p_question_order:
          data.question_order
      })
    }
  );

  const result =
    await readRpc(
      response,
      'Saving quiz question'
    );

  return result?.question;
}

async function fetchAssignmentTypeQuizQuestions(
  typeId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_type_quiz_questions?assignment_type_id=eq.${typeId}&select=*&order=question_order.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      `Reloading quiz questions failed (${response.status}): ${text}`
    );
  }

  if (!text) {
    return [];
  }

  const result =
    JSON.parse(text);

  return Array.isArray(result)
    ? result
    : [];
}

async function deleteQuizQuestion(
  id,
  cmsToken
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/assignment_library_delete_quiz_question`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY,
        'Content-Type':
          'application/json'
      },
      body: JSON.stringify({
        p_cms_token:
          cmsToken,
        p_question_id:
          id
      })
    }
  );

  await readRpc(
    response,
    'Deleting quiz question'
  );
}

async function saveCoreQuizQuestion(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_quiz_questions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  return response.json();
}

async function deleteCoreQuizQuestion(id) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_quiz_questions?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
}

function TypeForm({ data, setData, onSubmit, onCancel, isNew, saving }) {
  const fixedSubmissionKind =
    data.assignment_kind === 'peer_review' ||
    data.assignment_kind === 'structured_form';

  const groupsUnavailableKind =
    data.assignment_kind === 'peer_review';

  return (
    <form onSubmit={onSubmit} style={styles.form}>
      <div style={styles.field}>
        <label style={styles.label}>Name</label>
        <input
          style={styles.input}
          type="text"
          value={data.name}
          onChange={e => setData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. Essay, Presentation, Debate"
        />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Assignment kind</label>
        <select
          style={styles.input}
          value={data.assignment_kind || 'standard'}
          onChange={e => {
            const assignmentKind = e.target.value;
            setData(prev => ({
              ...prev,
              assignment_kind: assignmentKind,
              ...(
                assignmentKind === 'peer_review'
                  ? {
                      requires_submission: true,
                      allow_groups: false,
                      group_submission: false
                    }
                  : assignmentKind === 'structured_form'
                    ? {
                        requires_submission: true
                      }
                    : {}
              )
            }));
          }}
        >
          <option value="standard">Standard</option>
          <option value="peer_review">Peer Review</option>
          <option value="structured_form">Structured Form</option>
        </select>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>
          {
            data.assignment_kind === 'structured_form'
              ? 'General instructions (optional)'
              : 'Instructions'
          }
        </label>

        <RichTextEditor
          value={data.instructions || ''}
          onChange={val => setData(prev => ({ ...prev, instructions: val }))}
        />

        {data.assignment_kind === 'structured_form' && (
          <div
            style={{
              marginTop: '7px',
              color: '#66788a',
              fontSize: '12px',
              lineHeight: 1.45
            }}
          >
            The Structured Form itself is defined by its sections and
            questions. The Form editor opens immediately after this
            assignment type is created.
          </div>
        )}
      </div>
      <div style={styles.checkRow}>
        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={
              fixedSubmissionKind
                ? true
                : data.requires_submission
            }
            disabled={fixedSubmissionKind}
            onChange={e => setData(prev => ({ ...prev, requires_submission: e.target.checked }))}
            style={{ marginRight: '8px' }}
          />
          Requires submission
        </label>
      </div>

      <div style={styles.groupCheckRow}>
        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={!!data.allow_groups}
            disabled={groupsUnavailableKind}
            onChange={e => {
              const checked = e.target.checked;
              setData(prev => ({
                ...prev,
                allow_groups: checked,
                group_submission: checked
              }));
            }}
            style={{ marginRight: '8px' }}
          />
          Allow Groups
        </label>

        <label
          style={{
            ...styles.checkLabel,
            ...(!data.allow_groups ? styles.checkLabelDisabled : {})
          }}
        >
          <input
            type="checkbox"
            checked={
              !!data.allow_groups &&
              (
                data.assignment_kind === 'structured_form'
                  ? true
                  : !!data.group_submission
              )
            }
            disabled={
              !data.allow_groups ||
              groupsUnavailableKind ||
              data.assignment_kind === 'structured_form'
            }
            onChange={e => setData(prev => ({
              ...prev,
              group_submission: e.target.checked
            }))}
            style={{ marginRight: '8px' }}
          />
          Group Submission
        </label>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Default syllabus cooldown (minutes)</label>
        <input
          style={{ ...styles.input, maxWidth: '120px' }}
          type="number"
          min="0"
          step="0.1"
          value={data.syllabus_cooldown_minutes}
          onChange={e => setData(prev => ({ ...prev, syllabus_cooldown_minutes: e.target.value }))}
        />
      </div>
      <div style={styles.btnRow}>
        <button style={styles.btn} disabled={saving}>
          {saving ? 'Saving...' : (isNew ? 'Add Assignment Type' : 'Save Changes')}
        </button>
        <button type="button" style={styles.secondaryBtn} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}


function CoreAssignmentForm({
  data,
  setData,
  onSubmit,
  onCancel,
  saving
}) {
  const isQuestionImprovement =
    data.name === 'Question Improvement';

  return (
    <form
      onSubmit={onSubmit}
      style={styles.form}
    >
      <div style={styles.coreNote}>
        This is the global syllabus description for this built-in assignment.
        Editing it here changes the same system-wide text used by the Syllabus.
      </div>

      <div style={styles.field}>
        <label style={styles.label}>
          Name
        </label>

        <input
          style={{
            ...styles.input,
            background: '#f0f4f7'
          }}
          type="text"
          value={data.name || ''}
          readOnly
        />
      </div>

      {data.has_clarificatory_variants ? (
        <>
          <div style={styles.field}>
            <label style={styles.label}>
              Instructions — clarificatory questions enabled
            </label>

            <RichTextEditor
              value={
                data.content_with_clarificatory ||
                ''
              }
              onChange={val =>
                setData(prev => ({
                  ...prev,
                  content_with_clarificatory:
                    val
                }))
              }
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>
              Instructions — clarificatory questions not enabled
            </label>

            <RichTextEditor
              value={
                data.content_without_clarificatory ||
                ''
              }
              onChange={val =>
                setData(prev => ({
                  ...prev,
                  content_without_clarificatory:
                    val
                }))
              }
            />
          </div>
        </>
      ) : (
        <div style={styles.field}>
          <label style={styles.label}>
            Instructions
          </label>

          <RichTextEditor
            value={data.content || ''}
            onChange={val =>
              setData(prev => ({
                ...prev,
                content: val
              }))
            }
          />
        </div>
      )}

      {isQuestionImprovement && (
        <div style={styles.field}>
          <label style={styles.label}>
            Default syllabus cooldown (minutes)
          </label>

          <input
            style={{
              ...styles.input,
              maxWidth: '120px'
            }}
            type="number"
            min="0"
            step="0.1"
            value={
              data.cooldown_minutes ?? 0
            }
            onChange={e =>
              setData(prev => ({
                ...prev,
                cooldown_minutes:
                  e.target.value
              }))
            }
          />
        </div>
      )}

      <div style={styles.btnRow}>
        <button
          style={styles.btn}
          disabled={saving}
        >
          {saving
            ? 'Saving...'
            : isQuestionImprovement
              ? 'Save Global Settings'
              : 'Save Global Instructions'}
        </button>

        <button
          type="button"
          style={styles.secondaryBtn}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AssignmentLibrary({ session }) {
  const [types, setTypes] = useState([]);
  const [coreSections, setCoreSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editingCore, setEditingCore] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState({
    name: '',
    instructions: '',
    assignment_kind: 'standard',
    requires_submission: true,
    allow_groups: false,
    group_submission: false,
    syllabus_cooldown_minutes: 1
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedQuizId, setExpandedQuizId] = useState(null);
  const [expandedStructuredId, setExpandedStructuredId] = useState(null);
  const [newQuiz, setNewQuiz] = useState({});

  function canEditType(type) {
    return (
      session?.isSuperadmin === true ||
      (
        type?.instructor_id &&
        type.instructor_id ===
          session?.instructorId
      )
    );
  }

  function ownerLabel(type) {
    if (
      type?.instructor_id ===
      session?.instructorId
    ) {
      return 'Created by you';
    }

    if (type?.instructors?.name) {
      return `Created by ${type.instructors.name}`;
    }

    if (type?.instructor_id) {
      return 'Created by another instructor';
    }

    return 'Legacy assignment — admin managed';
  }

  useEffect(() => {
    Promise.all([
      fetchAssignmentTypes(),
      fetchCoreAssignmentSections()
    ])
      .then(([assignmentTypes, core]) => {
        setTypes(Array.isArray(assignmentTypes) ? assignmentTypes : []);
        setCoreSections(Array.isArray(core) ? core : []);
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleUpdateCore(e) {
  e.preventDefault();
  setError('');

  if (session?.isSuperadmin !== true) {
    setError(
      'Only a superadmin can change built-in assignment defaults.'
    );
    return;
  }

  if (!editingCore?.id) return;

  setSaving(true);

  try {
    const data =
      editingCore.has_clarificatory_variants
        ? {
            content_with_clarificatory:
              editingCore.content_with_clarificatory ||
              '',
            content_without_clarificatory:
              editingCore.content_without_clarificatory ||
              ''
          }
        : {
            content:
              editingCore.content || ''
          };

    if (
      editingCore.name ===
      'Question Improvement'
    ) {
      data.cooldown_minutes =
        Number(
          editingCore.cooldown_minutes ??
          0
        );
    }

    const result =
      await updateCoreAssignmentSection(
        editingCore.id,
        data
      );

    const updated =
      result?.[0] || {
        ...editingCore,
        ...data
      };

    setCoreSections(prev =>
      prev.map(section =>
        section.id === editingCore.id
          ? {
              ...section,
              ...updated,
              syllabus_quiz_questions:
                section.syllabus_quiz_questions ||
                []
            }
          : section
      )
    );

    setEditingCore(null);

    setSuccess(
      editingCore.name ===
        'Question Improvement'
        ? 'Global Question Improvement settings updated.'
        : 'Global assignment instructions updated.'
    );
  } catch (err) {
    setError(
      'Failed to update global assignment settings.'
    );
  } finally {
    setSaving(false);
  }
}

  async function handleAdd(e) {
    e.preventDefault();
    setError('');
    if (!newType.name.trim()) return setError('Enter a name for this assignment type.');
    setSaving(true);
    try {
      const createdType =
        await saveAssignmentType(
        {
          name: newType.name.trim(),
          instructions:
            newType.instructions,
          is_core: false,
          assignment_kind:
            newType.assignment_kind ||
            'standard',
          requires_submission:
            (
              newType.assignment_kind ===
                'peer_review' ||
              newType.assignment_kind ===
                'structured_form'
            )
              ? true
              : newType.requires_submission,
          allow_groups:
            newType.assignment_kind ===
              'peer_review'
              ? false
              : !!newType.allow_groups,
          group_submission:
            newType.assignment_kind ===
              'peer_review'
              ? false
              : newType.assignment_kind ===
                  'structured_form'
                ? !!newType.allow_groups
                : !!newType.allow_groups &&
                  !!newType.group_submission,
          syllabus_cooldown_minutes:
            Number(
              newType
                .syllabus_cooldown_minutes
            )
        },
        session.cmsToken
      );

      const refreshed =
        await fetchAssignmentTypes();

      setTypes(
        Array.isArray(refreshed)
          ? refreshed
          : []
      );
      setNewType({
        name: '',
        instructions: '',
        assignment_kind: 'standard',
        requires_submission: true,
        allow_groups: false,
        group_submission: false,
        syllabus_cooldown_minutes: 1
      });
      setAdding(false);

      if (
        createdType?.assignment_kind ===
          'structured_form' &&
        createdType?.id
      ) {
        setExpandedStructuredId(
          createdType.id
        );
        setExpandedQuizId(null);
      }

      setSuccess(
        createdType?.assignment_kind ===
          'structured_form'
          ? 'Structured Form created. Configure its sections and questions below.'
          : 'Assignment type added.'
      );
    } catch (err) {
      console.error(
        'Assignment type save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save assignment type.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    setError('');
    if (!editing.name.trim()) return setError('Enter a name.');

    if (!canEditType(editing)) {
      return setError(
        'Only the instructor who created this assignment type can edit it.'
      );
    }

    setSaving(true);
    try {
      const result = await updateAssignmentType(editing.id, {
        name: editing.name.trim(),
        instructions: editing.instructions,
        assignment_kind: editing.assignment_kind || 'standard',
        requires_submission:
          (
            editing.assignment_kind === 'peer_review' ||
            editing.assignment_kind === 'structured_form'
          )
            ? true
            : editing.requires_submission,
        allow_groups:
          editing.assignment_kind ===
            'peer_review'
            ? false
            : !!editing.allow_groups,
        group_submission:
          editing.assignment_kind ===
            'peer_review'
            ? false
            : editing.assignment_kind ===
                'structured_form'
              ? !!editing.allow_groups
              : !!editing.allow_groups &&
                !!editing.group_submission,
        syllabus_cooldown_minutes: Number(editing.syllabus_cooldown_minutes)
      }, session.cmsToken);
      setTypes(prev => prev.map(t =>
        t.id === editing.id
          ? { ...result, assignment_type_quiz_questions: t.assignment_type_quiz_questions }
          : t
      ));
      setEditing(null);
      setSuccess('Assignment type updated.');
    } catch (err) {
      console.error(
        'Assignment type update failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to update assignment type.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(type) {
    if (!canEditType(type)) {
      setError(
        'Only the instructor who created this assignment type can delete it.'
      );
      return;
    }

    if (!window.confirm(`Delete "${type.name}"? This cannot be undone.`)) return;
    await deleteAssignmentType(
      type.id,
      session.cmsToken
    );
    setTypes(prev => prev.filter(t => t.id !== type.id));
    setSuccess('Assignment type deleted.');
  }

  async function handleAddQuizQuestion(
  typeId
) {
  const q =
    newQuiz[typeId] || {};

  if (!q.question?.trim()) {
    return setError(
      'Enter a question.'
    );
  }

  const choices =
    (q.choices || '')
      .split('\n')
      .map(c => c.trim())
      .filter(Boolean);

  if (choices.length < 2) {
    return setError(
      'Enter at least two choices.'
    );
  }

  if (
    q.correct_answer_index ===
      undefined ||
    q.correct_answer_index === ''
  ) {
    return setError(
      'Select the correct answer.'
    );
  }

  setSaving(true);
  setError('');
  setSuccess('');

  try {
    const existing =
      types.find(
        t => t.id === typeId
      )
        ?.assignment_type_quiz_questions ||
      [];

    const type =
      types.find(
        item => item.id === typeId
      );

    if (!canEditType(type)) {
      throw new Error(
        'Only the instructor who created this assignment type can change its default quiz.'
      );
    }

    await saveQuizQuestion({
      assignment_type_id:
        typeId,

      question:
        q.question.trim(),

      choices:
        JSON.stringify(
          choices
        ),

      correct_answer_index:
        Number(
          q.correct_answer_index
        ),

      correct_answer:
        choices[
          Number(
            q.correct_answer_index
          )
        ],

      question_order:
        existing.length + 1
    }, session.cmsToken);

    /*
     * Do not trust local state here.
     * Read the actual saved rows back
     * from Supabase.
     */
    const savedQuestions =
      await fetchAssignmentTypeQuizQuestions(
        typeId
      );

    setTypes(prev =>
      prev.map(type =>
        type.id === typeId
          ? {
              ...type,
              assignment_type_quiz_questions:
                savedQuestions
            }
          : type
      )
    );

    setNewQuiz(prev => ({
      ...prev,
      [typeId]: {
        question: '',
        choices: '',
        correct_answer_index: ''
      }
    }));

    setSuccess(
      'Quiz question added.'
    );
  } catch (err) {
    console.error(
      'Failed to add default quiz question:',
      err
    );

    setError(
      err?.message ||
        'Failed to add quiz question.'
    );
  } finally {
    setSaving(false);
  }
}

  async function handleDeleteQuizQuestion(typeId, questionId) {
    const type =
      types.find(
        item => item.id === typeId
      );

    if (!canEditType(type)) {
      setError(
        'Only the instructor who created this assignment type can change its default quiz.'
      );
      return;
    }

    await deleteQuizQuestion(
      questionId,
      session.cmsToken
    );
    setTypes(prev => prev.map(t =>
      t.id === typeId
        ? { ...t, assignment_type_quiz_questions: t.assignment_type_quiz_questions.filter(q => q.id !== questionId) }
        : t
    ));
    setSuccess('Quiz question deleted.');
  }
async function handleAddCoreQuizQuestion(
  sectionId
) {
  if (session?.isSuperadmin !== true) {
    setError(
      'Only a superadmin can change built-in assignment defaults.'
    );
    return;
  }

  const q =
    newQuiz[sectionId] || {};

  if (!q.question?.trim()) {
    return setError(
      'Enter a question.'
    );
  }

  const choices =
    (q.choices || '')
      .split('\n')
      .map(c => c.trim())
      .filter(Boolean);

  if (choices.length < 2) {
    return setError(
      'Enter at least two choices.'
    );
  }

  if (
    q.correct_answer_index ===
      undefined ||
    q.correct_answer_index === ''
  ) {
    return setError(
      'Select the correct answer.'
    );
  }

  setSaving(true);

  try {
    const section =
      coreSections.find(
        item =>
          item.id === sectionId
      );

    const existing =
      section
        ?.syllabus_quiz_questions ||
      [];

    const result =
      await saveCoreQuizQuestion({
        syllabus_section_id:
          sectionId,
        question:
          q.question.trim(),
        choices:
          JSON.stringify(choices),
        correct_answer_index:
          Number(
            q.correct_answer_index
          ),
        correct_answer:
          choices[
            Number(
              q.correct_answer_index
            )
          ],
        question_order:
          existing.length + 1
      });

    const created =
      Array.isArray(result)
        ? result[0]
        : null;

    if (!created) {
      throw new Error(
        'No quiz question was returned.'
      );
    }

    await updateCoreAssignmentSection(
      sectionId,
      {
        has_quiz: true
      }
    );

    setCoreSections(prev =>
      prev.map(section =>
        section.id === sectionId
          ? {
              ...section,
              has_quiz: true,
              syllabus_quiz_questions: [
                ...(
                  section
                    .syllabus_quiz_questions ||
                  []
                ),
                created
              ]
            }
          : section
      )
    );

    setNewQuiz(prev => ({
      ...prev,
      [sectionId]: {
        question: '',
        choices: '',
        correct_answer_index: ''
      }
    }));

    setSuccess(
      'Default Question Improvement quiz question added.'
    );
  } catch (err) {
    setError(
      'Failed to add Question Improvement quiz question.'
    );
  } finally {
    setSaving(false);
  }
}

async function handleDeleteCoreQuizQuestion(
  sectionId,
  questionId
) {
  if (session?.isSuperadmin !== true) {
    setError(
      'Only a superadmin can change built-in assignment defaults.'
    );
    return;
  }

  try {
    await deleteCoreQuizQuestion(
      questionId
    );

    const section =
      coreSections.find(
        item =>
          item.id === sectionId
      );

    const remaining =
      (
        section
          ?.syllabus_quiz_questions ||
        []
      ).filter(
        question =>
          question.id !==
          questionId
      );

    if (remaining.length === 0) {
      await updateCoreAssignmentSection(
        sectionId,
        {
          has_quiz: false
        }
      );
    }

    setCoreSections(prev =>
      prev.map(section =>
        section.id === sectionId
          ? {
              ...section,
              has_quiz:
                remaining.length > 0,
              syllabus_quiz_questions:
                remaining
            }
          : section
      )
    );

    setSuccess(
      'Quiz question deleted.'
    );
  } catch (err) {
    setError(
      'Failed to delete quiz question.'
    );
  }
}
   if (loading) return <div style={styles.muted}>Loading...</div>;

return (
  <div style={styles.page}>
      <h2 style={styles.subheading}>Assignment Library</h2>
      <p style={styles.muted}>
  Edit the global defaults for every assignment type here. Assignment
  instructions, syllabus cooldowns, and syllabus quizzes are inherited by
  courses unless they are overridden in that course's Syllabus settings.
  Built-in assignments use the same system-wide descriptions that appear
  in the Syllabus.
</p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <h3 style={styles.sectionHeading}>Built-in Assignments</h3>

      <div style={styles.list}>
        {coreSections.length === 0 && (
          <div style={styles.muted}>
            No built-in assignment descriptions were found.
          </div>
        )}

        {coreSections.map(section => {
  const isEditingCore =
    editingCore?.id ===
    section.id;

  const isQuestionImprovement =
    section.name ===
    'Question Improvement';

  const isCoreQuizExpanded =
    expandedQuizId ===
    section.id;

  const coreQuizQuestions =
    section
      .syllabus_quiz_questions ||
    [];

  const nq =
    newQuiz[section.id] || {};

  const preview =
    section.has_clarificatory_variants
      ? (
          section.content_with_clarificatory ||
          section.content_without_clarificatory ||
          ''
        )
      : (
          section.content || ''
        );

  return (
    <div
      key={`core-${section.id}`}
      style={styles.item}
    >
      {isEditingCore ? (
        <CoreAssignmentForm
          data={editingCore}
          setData={setEditingCore}
          onSubmit={
            handleUpdateCore
          }
          onCancel={() =>
            setEditingCore(null)
          }
          saving={saving}
        />
      ) : (
        <>
          <div
            style={
              styles.itemInner
            }
          >
            <div
              style={
                styles.itemLeft
              }
            >
              <div
                style={
                  styles.itemName
                }
              >
                {section.name}
              </div>

              <div
                style={
                  styles.itemMeta
                }
              >
                <span
                  style={
                    styles.pillCore
                  }
                >
                  Built-in
                </span>

                {section.has_clarificatory_variants && (
                  <span
                    style={
                      styles.pillGrey
                    }
                  >
                    Two syllabus variants
                  </span>
                )}

                {isQuestionImprovement && (
                  <span
                    style={
                      styles.pillGrey
                    }
                  >
                    {section.cooldown_minutes ??
                      0}
                    min cooldown
                  </span>
                )}

                {isQuestionImprovement &&
                  coreQuizQuestions.length >
                    0 && (
                    <span
                      style={
                        styles.pillGreen
                      }
                    >
                      {
                        coreQuizQuestions.length
                      }{' '}
                      quiz question
                      {coreQuizQuestions.length !==
                      1
                        ? 's'
                        : ''}
                    </span>
                  )}
              </div>

              {preview ? (
                <div
                  style={
                    styles.instructionsPreview
                  }
                  dangerouslySetInnerHTML={{
                    __html: preview
                  }}
                />
              ) : (
                <div
                  style={
                    styles.muted
                  }
                >
                  No global instructions
                  yet.
                </div>
              )}
            </div>

            <div
              style={
                styles.itemActions
              }
            >
              {session?.isSuperadmin === true && (
              <button
                style={
                  styles.editBtn
                }
                onClick={() => {
                  setEditingCore({
                    ...section
                  });
                  setEditing(null);
                  setAdding(false);
                  setExpandedQuizId(
                    null
                  );
                  setError('');
                  setSuccess('');
                }}
              >
                Edit
              </button>
              )}

              {isQuestionImprovement && (
                <button
                  style={
                    styles.quizBtn
                  }
                  onClick={() =>
                    setExpandedQuizId(
                      isCoreQuizExpanded
                        ? null
                        : section.id
                    )
                  }
                >
                  {isCoreQuizExpanded
                    ? 'Hide Default Quiz'
                    : 'Default Quiz'}
                </button>
              )}
            </div>
          </div>

          {isQuestionImprovement &&
            isCoreQuizExpanded && (
            <div
              style={
                styles.quizSection
              }
            >
              <h4
                style={
                  styles.quizHeading
                }
              >
                Default Quiz Questions
              </h4>

              {coreQuizQuestions.map(
                q => {
                  const choices =
                    (() => {
                      try {
                        return JSON.parse(
                          q.choices ||
                          '[]'
                        );
                      } catch {
                        return [];
                      }
                    })();

                  return (
                    <div
                      key={q.id}
                      style={
                        styles.quizItem
                      }
                    >
                      <div
                        style={
                          styles.quizItemLeft
                        }
                      >
                        <div
                          style={
                            styles.quizQuestion
                          }
                        >
                          {q.question}
                        </div>

                        <div
                          style={
                            styles.quizChoices
                          }
                        >
                          {choices.map(
                            (
                              choice,
                              index
                            ) => (
                              <div
                                key={
                                  index
                                }
                                style={{
                                  ...styles.quizChoice,
                                  ...(index ===
                                  q.correct_answer_index
                                    ? styles.quizChoiceCorrect
                                    : {})
                                }}
                              >
                                {index ===
                                q.correct_answer_index
                                  ? '✓ '
                                  : '○ '}
                                {choice}
                              </div>
                            )
                          )}
                        </div>
                      </div>

                      {session?.isSuperadmin === true && (
                        <button
                          style={
                            styles.dangerBtn
                          }
                          onClick={() =>
                            handleDeleteCoreQuizQuestion(
                              section.id,
                              q.id
                            )
                          }
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  );
                }
              )}

              {session?.isSuperadmin === true && (
              <div
                style={
                  styles.addQuizForm
                }
              >
                <div
                  style={
                    styles.field
                  }
                >
                  <label
                    style={
                      styles.label
                    }
                  >
                    Question
                  </label>

                  <input
                    style={
                      styles.input
                    }
                    type="text"
                    value={
                      nq.question ||
                      ''
                    }
                    onChange={e =>
                      setNewQuiz(
                        prev => ({
                          ...prev,
                          [section.id]:
                            {
                              ...prev[
                                section.id
                              ],
                              question:
                                e.target
                                  .value
                            }
                        })
                      )
                    }
                    placeholder="Enter quiz question..."
                  />
                </div>

                <div
                  style={
                    styles.field
                  }
                >
                  <label
                    style={
                      styles.label
                    }
                  >
                    Answer choices
                    (one per line)
                  </label>

                  <textarea
                    style={
                      styles.textarea
                    }
                    value={
                      nq.choices || ''
                    }
                    onChange={e =>
                      setNewQuiz(
                        prev => ({
                          ...prev,
                          [section.id]:
                            {
                              ...prev[
                                section.id
                              ],
                              choices:
                                e.target
                                  .value
                            }
                        })
                      )
                    }
                    rows={4}
                    placeholder={
                      'Yes\nNo\nSometimes\nNever'
                    }
                  />
                </div>

                <div
                  style={
                    styles.field
                  }
                >
                  <label
                    style={
                      styles.label
                    }
                  >
                    Correct answer
                  </label>

                  <select
                    style={
                      styles.input
                    }
                    value={
                      nq.correct_answer_index ??
                      ''
                    }
                    onChange={e =>
                      setNewQuiz(
                        prev => ({
                          ...prev,
                          [section.id]:
                            {
                              ...prev[
                                section.id
                              ],
                              correct_answer_index:
                                e
                                  .target
                                  .value ===
                                ''
                                  ? ''
                                  : Number(
                                      e
                                        .target
                                        .value
                                    )
                            }
                        })
                      )
                    }
                  >
                    <option value="">
                      Select correct
                      answer...
                    </option>

                    {(nq.choices ||
                      '')
                      .split('\n')
                      .map(c =>
                        c.trim()
                      )
                      .filter(Boolean)
                      .map(
                        (
                          choice,
                          index
                        ) => (
                          <option
                            key={
                              index
                            }
                            value={
                              index
                            }
                          >
                            {choice}
                          </option>
                        )
                      )}
                  </select>
                </div>

                <button
                  type="button"
                  style={
                    styles.secondaryBtn
                  }
                  disabled={saving}
                  onClick={() =>
                    handleAddCoreQuizQuestion(
                      section.id
                    )
                  }
                >
                  Add Quiz Question
                </button>
              </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
})}
      </div>

      <h3 style={styles.sectionHeading}>Other Assignment Types</h3>

      <div style={styles.list}>
        {types.length === 0 && !adding && (
          <div style={styles.muted}>No assignment types added yet.</div>
        )}

        {types.map(type => {
          const canEdit =
            canEditType(type);

          const isEditing =
            editing?.id === type.id &&
            canEdit;

          const isQuizExpanded =
            expandedQuizId === type.id;

          const isStructuredForm =
            type.assignment_kind ===
            'structured_form';

          const isStructuredExpanded =
            expandedStructuredId ===
            type.id;

          const nq = newQuiz[type.id] || {};
          const quizQuestions = type.assignment_type_quiz_questions || [];

          return (
            <div key={type.id} style={styles.item}>
              {isEditing ? (
                <TypeForm
                  data={editing}
                  setData={setEditing}
                  onSubmit={handleUpdate}
                  onCancel={() => setEditing(null)}
                  isNew={false}
                />
              ) : (
                <>
                  <div style={styles.itemInner}>
                    <div style={styles.itemLeft}>
                      <div style={styles.itemName}>{type.name}</div>
                      <div style={styles.itemMeta}>
                        <span style={type.requires_submission ? styles.pillBlue : styles.pillGrey}>
                          {type.requires_submission ? 'Submission' : 'No submission'}
                        </span>

                        {isStructuredForm && (
                          <span style={styles.pillPurple}>
                            Structured Form
                          </span>
                        )}
                        {type.allow_groups && (
                          <span style={styles.pillPurple}>
                            Groups allowed
                          </span>
                        )}
                        {type.allow_groups && type.group_submission && (
                          <span style={styles.pillGreen}>
                            Group submission
                          </span>
                        )}
                        <span style={styles.pillGrey}>
                          {type.syllabus_cooldown_minutes}min cooldown
                        </span>

                        <span style={styles.pillGrey}>
                          {ownerLabel(type)}
                        </span>
                        {quizQuestions.length > 0 && (
                          <span style={styles.pillGreen}>
                            {quizQuestions.length} quiz question{quizQuestions.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {type.instructions && (
                        <div
                          style={styles.instructionsPreview}
                          dangerouslySetInnerHTML={{ __html: type.instructions }}
                        />
                      )}
                    </div>
                    <div style={styles.itemActions}>
                      {canEdit && (
                      <button
                        style={styles.editBtn}
                        onClick={() => {
                          setEditing({
  ...type,
  assignment_kind: type.assignment_kind || 'standard'
});
setEditingCore(null);
                          setAdding(false);
                          setExpandedQuizId(null);
                          setExpandedStructuredId(null);
                          setError('');
                          setSuccess('');
                        }}
                      >
                        Edit
                      </button>
                      )}
                      {/* STRUCTURED_FORM_DEFAULT_QUIZ_PATCH */}

                      {isStructuredForm && (
                        <button
                          style={styles.quizBtn}
                          onClick={() => {
                            setExpandedStructuredId(
                              isStructuredExpanded
                                ? null
                                : type.id
                            );

                            setExpandedQuizId(null);
                          }}
                        >
                          {isStructuredExpanded
                            ? 'Hide Form'
                            : 'Edit Form'}
                        </button>
                      )}

                      <button
                        style={styles.quizBtn}
                        onClick={() => {
                          setExpandedQuizId(
                            isQuizExpanded
                              ? null
                              : type.id
                          );

                          setExpandedStructuredId(null);
                        }}
                      >
                        {isQuizExpanded
                          ? 'Hide Default Quiz'
                          : 'Default Quiz'}
                      </button>
                      {canEdit && (
                        <button
                          style={styles.dangerBtn}
                          onClick={() =>
                            handleDelete(type)
                          }
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {isStructuredForm &&
                    isStructuredExpanded && (
                      <StructuredFormBuilder
                        mode="library"
                        type={type}
                        session={session}
                        canEdit={canEdit}
                      />
                    )}

                  {isQuizExpanded && (
                    <div style={styles.quizSection}>
                      <h4 style={styles.quizHeading}>Default Quiz Questions</h4>

                      {quizQuestions.map(q => {
                        const choices = (() => { try { return JSON.parse(q.choices || '[]'); } catch { return []; } })();
                        return (
                          <div key={q.id} style={styles.quizItem}>
                            <div style={styles.quizItemLeft}>
                              <div style={styles.quizQuestion}>{q.question}</div>
                              <div style={styles.quizChoices}>
                                {choices.map((c, i) => (
                                  <div key={i} style={{
                                    ...styles.quizChoice,
                                    ...(i === q.correct_answer_index ? styles.quizChoiceCorrect : {})
                                  }}>
                                    {i === q.correct_answer_index ? '✓ ' : '○ '}{c}
                                  </div>
                                ))}
                              </div>
                            </div>
                            {canEdit && (
                              <button
                                style={styles.dangerBtn}
                                onClick={() =>
                                  handleDeleteQuizQuestion(
                                    type.id,
                                    q.id
                                  )
                                }
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        );
                      })}

                      {canEdit ? (
                      <div style={styles.addQuizForm}>
                        <div style={styles.field}>
                          <label style={styles.label}>Question</label>
                          <input
                            style={styles.input}
                            type="text"
                            value={nq.question || ''}
                            onChange={e => setNewQuiz(prev => ({
                              ...prev,
                              [type.id]: { ...prev[type.id], question: e.target.value }
                            }))}
                            placeholder="Enter quiz question..."
                          />
                        </div>
                        <div style={styles.field}>
                          <label style={styles.label}>Answer choices (one per line)</label>
                          <textarea
                            style={styles.textarea}
                            value={nq.choices || ''}
                            onChange={e => setNewQuiz(prev => ({
                              ...prev,
                              [type.id]: { ...prev[type.id], choices: e.target.value }
                            }))}
                            rows={4}
                            placeholder={'Yes\nNo\nSometimes\nNever'}
                          />
                        </div>
                        <div style={styles.field}>
                          <label style={styles.label}>Correct answer</label>
                          <select
                            style={styles.input}
                            value={nq.correct_answer_index ?? ''}
                            onChange={e => setNewQuiz(prev => ({
                              ...prev,
                              [type.id]: { ...prev[type.id], correct_answer_index: Number(e.target.value) }
                            }))}
                          >
                            <option value="">Select correct answer...</option>
                            {(nq.choices || '').split('\n').map(c => c.trim()).filter(Boolean).map((c, i) => (
                              <option key={i} value={i}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          style={styles.secondaryBtn}
                          disabled={saving}
                          onClick={() => handleAddQuizQuestion(type.id)}
                        >
                          Add Quiz Question
                        </button>
                      </div>
                      ) : (
                        <div style={styles.muted}>
                          View only — only the instructor who created this assignment type can change its default quiz.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {adding ? (
        <div style={styles.addBox}>
          <h3 style={styles.addHeading}>New Assignment Type</h3>
          <TypeForm
            data={newType}
            setData={setNewType}
            onSubmit={handleAdd}
            onCancel={() => {
              setAdding(false);
              setNewType({
                name: '',
                instructions: '',
                assignment_kind: 'standard',
                requires_submission: true,
                allow_groups: false,
                group_submission: false,
                syllabus_cooldown_minutes: 1
              });
            }}
            isNew={true}
          />
        </div>
      ) : (
        <button
          style={styles.addBtn}
          onClick={() => { setAdding(true); setEditing(null); setEditingCore(null); setExpandedQuizId(null); setExpandedStructuredId(null); setError(''); setSuccess(''); }}
        >
          + Add Assignment Type
        </button>
      )}
    </div>
  );
}

const styles = {
  page: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box'
  },
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  sectionHeading: { margin: '22px 0 10px', fontSize: '15px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '8px 0 20px' },
  coreNote: {
    background: '#f0f4f7',
    borderRadius: '8px',
    padding: '10px 12px',
    color: '#536575',
    fontSize: '13px',
    lineHeight: '1.45',
    marginBottom: '16px'
  },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  list: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' },
  item: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '16px', background: 'white'
  },
  itemInner: { display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' },
  itemLeft: { flex: 1, minWidth: 0 },
  itemName: { fontWeight: '700', fontSize: '15px', color: '#1f2933', marginBottom: '6px' },
  itemMeta: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' },
  pillBlue: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  pillCore: {
    background: '#f1ebf7', color: '#65457d', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  pillGrey: {
    background: '#f0f4f7', color: '#66788a', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  pillGreen: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  pillPurple: {
    background: '#eee8f8', color: '#5a4383', borderRadius: '999px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '700'
  },
  instructionsPreview: {
    fontSize: '13px', color: '#66788a', lineHeight: '1.5',
    overflow: 'hidden', maxHeight: '60px',
    maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)'
  },
  itemActions: { display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' },
  form: { width: '100%' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  textarea: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box',
    background: 'white', resize: 'vertical'
  },
  checkRow: { marginBottom: '16px' },
  groupCheckRow: {
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap'
  },
  checkLabel: { fontSize: '14px', color: '#1f2933', display: 'flex', alignItems: 'center', cursor: 'pointer' },
  checkLabelDisabled: { color: '#a0aab4', cursor: 'not-allowed' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '4px' },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  secondaryBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer'
  },
  editBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  quizBtn: {
    background: 'transparent', color: '#216e45', border: '1px solid #b9dfc9',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  addBox: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '20px',
    background: '#f7f9fa', marginTop: '8px'
  },
  addHeading: { margin: '0 0 16px', fontSize: '16px', color: '#1f2933' },
  addBtn: {
    background: 'transparent', color: '#215b7d', border: '2px dashed #b0cfe0',
    borderRadius: '10px', padding: '12px 20px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', width: '100%', marginTop: '4px'
  },
  quizSection: {
    marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #d7dee5'
  },
  quizHeading: { margin: '0 0 12px', fontSize: '14px', color: '#1f2933', fontWeight: '700' },
  quizItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: '12px', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '10px 12px', marginBottom: '8px', background: '#f7f9fa'
  },
  quizItemLeft: { flex: 1 },
  quizQuestion: { fontWeight: '600', fontSize: '14px', color: '#1f2933', marginBottom: '6px' },
  quizChoices: { display: 'flex', flexDirection: 'column', gap: '3px' },
  quizChoice: { fontSize: '13px', color: '#66788a' },
  quizChoiceCorrect: { color: '#216e45', fontWeight: '700' },
  addQuizForm: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '14px',
    background: 'white', marginTop: '10px'
  }
};